/**
 * Static societies snapshot for the admin console Societies page.
 *
 * Baked-in list of societies extracted from the local `ubond` MySQL
 * database (tables `societies` + `membership`) so the page renders without
 * an API round-trip.
 *
 * - Selection: the most-followed societies that actually have an icon
 *   image (`societies.icon` non-null and non-empty). Cloudinary URLs
 *   (`res.cloudinary.com`) are preferred over expiring Instagram CDN URLs;
 *   all entries here use stable Cloudinary icons.
 * - Exclusion: the society "新亞書院橋友 / New Asia Sowk" is intentionally
 *   never included — its stored icon URL
 *   (`.../organizer_icon/ca772b5e-3727-4a12-affd-304a121ab74f.jpg`) is a
 *   shared Instagram CDN avatar that does not belong to it. The selection
 *   query therefore adds `AND icon NOT LIKE '%ca772b5e%'` on top of the
 *   Cloudinary-only filter, so it can never appear in this list.
 * - `memberCount` is the COUNT of rows in `membership` per society
 *   (mirrors the backend `getAllSocieties` `_count.members`).
 * - `instagramFollowers` is the society's Instagram follower count
 *   (`societies.instagram_followers`); null when absent. This is the figure
 *   the marketing landing page displays (e.g. "CUHK · 783 members").
 * - `icon` is the society's icon URL (`societies.icon`); never null here.
 * - `university` is the raw university code (e.g. "cuhk", "cityu").
 */
export type StaticSociety = {
  id: string;
  name: string;
  university: string | null;
  memberCount: number;
  instagramFollowers: number | null;
  icon: string | null;
};

/** Embedded societies list (exactly 42 entries) — no API call required. */
export const STATIC_SOCIETIES: StaticSociety[] = [  {
    id: "08baa373-4938-43c4-be06-05e85651d9dd",
    name: "City University of Hong Kong",
    university: "cityu",
    memberCount: 0,
    instagramFollowers: 1639,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1764348216/organizer_icon/bca5656d-8d9a-4c49-aee3-c40fd45ed18c.jpg",
  },
  {
    id: "72bf5540-a8ab-4227-88f7-598bc500b916",
    name: "The Education University of Hong Kong, EdUHK",
    university: "eduhk",
    memberCount: 0,
    instagramFollowers: 1620,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1765894480/organizer_icon/f67fbe68-c2f3-4522-bec5-560a6b5c36e3.jpg",
  },
  {
    id: "8fe42fc9-fa45-4efb-8a58-fa56817b6ede",
    name: "CUHK CTT",
    university: "cuhk",
    memberCount: 0,
    instagramFollowers: 361,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770565133/organizer_icon/e8ebe2cc-6cfb-4ddb-9df1-eeda2deb302f.jpg",
  },
  {
    id: "5c4f6c98-c463-4a8a-9525-480b85f0cf5f",
    name: "嶺南大學基督徒團契 LUCF",
    university: "lingu",
    memberCount: 0,
    instagramFollowers: 329,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770564963/organizer_icon/b0995f14-f273-4831-8161-9ffbca7e2e75.jpg",
  },
  {
    id: "210a3255-8424-419a-ac58-797096f13322",
    name: "Faculty of Social Science",
    university: "cuhk",
    memberCount: 0,
    instagramFollowers: 309,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1764395679/organizer_icon/ef2ee01c-10b8-4c2c-9ddd-94bbe7298457.jpg",
  },
  {
    id: "e1a7d372-9b34-4ea1-9237-93657341ce0b",
    name: "🏙🔍🚞🐂 港大地理 HKU GGAS 🌍🌦🌱🌋",
    university: "hku",
    memberCount: 0,
    instagramFollowers: 250,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770565198/organizer_icon/dd88f0e6-5ae0-4130-9244-3d65402fc382.jpg",
  },
  {
    id: "e7f8baab-79ee-4fdd-a573-114adee02f33",
    name: "𝙴 𝚍 𝚄 𝙷 𝙺 𝙲𝚑𝚒𝚗𝚎𝚜𝚎 𝙾𝚛𝚌𝚑𝚎𝚜𝚝𝚛𝚊",
    university: "eduhk",
    memberCount: 0,
    instagramFollowers: 235,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770564972/organizer_icon/c5815ff7-bbc4-46c2-9f4f-99d1782d0c9e.jpg",
  },
  {
    id: "68e9c6e0-0e77-4323-b374-d213a97c66f8",
    name: "HKBU Communication Society",
    university: "hkbu",
    memberCount: 0,
    instagramFollowers: 534,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1765783113/organizer_icon/d7b4efe9-95ee-4db1-b68d-a3fc0d56c56b.jpg",
  },
  {
    id: "e183eb54-c1df-4fa8-b030-eb13f89e2db8",
    name: "St. John's College",
    university: "hku",
    memberCount: 0,
    instagramFollowers: 507,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1764394698/organizer_icon/edcc9d8d-81d7-4bf0-8344-a7900079e10e.jpg",
  },
  {
    id: "87817e36-dca8-4124-b3f0-3e75a0b9f98b",
    name: "港大社工 HKU Social Work",
    university: "hku",
    memberCount: 0,
    instagramFollowers: 365,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770565167/organizer_icon/a3d7a153-4ad9-447c-ab21-bec157d738ff.jpg",
  },
  {
    id: "e5cdaec6-2f07-4fc9-aae8-273f894190e8",
    name: "HKU Architecture",
    university: "hku",
    memberCount: 0,
    instagramFollowers: 1380,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1764395685/organizer_icon/284394be-9f6c-4720-be8e-dd5e2c09818c.jpg",
  },
  {
    id: "f76ef497-f15c-4150-a3c1-ecf72e5a9ec5",
    name: "HKU Business School",
    university: "hku",
    memberCount: 0,
    instagramFollowers: 2367,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1764395699/organizer_icon/46316faf-215d-417e-b3fb-c1e14cc2d862.jpg",
  },
  {
    id: "739e3f3b-6e55-44b3-944f-ec3ff92e4fe3",
    name: "瀠文洄宗 Rivucia",
    university: "cuhk",
    memberCount: 0,
    instagramFollowers: 225,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770565097/organizer_icon/ff1a92bc-27cb-4437-ab81-98553ca64841.jpg",
  },
  {
    id: "d0aab835-bb19-4b7b-8732-3e3f526547fb",
    name: "linguistics_eduhk",
    university: "eduhk",
    memberCount: 0,
    instagramFollowers: 320,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770564881/organizer_icon/ef02ee0b-038d-4339-8749-d705d462609c.jpg",
  },
  {
    id: "5baff5fb-351a-49b6-ad91-fdb34704d5e7",
    name: "University YMCA (HKBU)",
    university: "hkbu",
    memberCount: 0,
    instagramFollowers: 1048,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1764395713/organizer_icon/06faf7c8-51c0-4516-9c93-4200860b1b5f.jpg",
  },
  {
    id: "43d3fa54-ec6d-4a0f-80e0-1a19f850ec50",
    name: "臺中高工學生自治會",
    university: null,
    memberCount: 0,
    instagramFollowers: 271,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770565148/organizer_icon/872319ca-fa5b-4811-a2b9-adcfc9914102.jpg",
  },
  {
    id: "b81f63cf-ce51-4ed7-842c-a9b8d5c76bbe",
    name: "🦊28th LUDS Silva",
    university: "lingu",
    memberCount: 0,
    instagramFollowers: 819,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1764395719/organizer_icon/d02b512b-f520-47ba-ad52-39492719b7fd.jpg",
  },
  {
    id: "e5e488ba-ff63-42a7-a16b-54de15b79a87",
    name: "HKMU HLT",
    university: "hkmu",
    memberCount: 0,
    instagramFollowers: 252,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1765781105/organizer_icon/1ab5aa05-1502-4c98-9d41-5701eb7971c6.jpg",
  },
  {
    id: "399542e6-e2d7-4be6-94f5-fe98c07024f1",
    name: "崇基劇社",
    university: null,
    memberCount: 0,
    instagramFollowers: 252,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770565221/organizer_icon/fd8a5ac3-a44b-4dff-924b-d549e09b65bb.jpg",
  },
  {
    id: "0660f573-54fd-4f90-8bf7-e7bef7f5ea4d",
    name: "cuhknursing",
    university: "cuhk",
    memberCount: 0,
    instagramFollowers: 73,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1765781108/organizer_icon/d9865998-5775-47f2-8fca-5e39e0d29aa4.jpg",
  },
  {
    id: "6f1546cd-2016-4956-80b0-87a139d4cce7",
    name: "HKUST MBA",
    university: "hku",
    memberCount: 0,
    instagramFollowers: 923,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770564924/organizer_icon/476546ec-3036-470d-85ab-a5439842530f.jpg",
  },
  {
    id: "4bd34009-3850-4aec-a386-b75828331470",
    name: "HKJSASU",
    university: null,
    memberCount: 0,
    instagramFollowers: 263,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1765781116/organizer_icon/70ad9486-e358-4bcc-a9ed-bbcd45652b08.jpg",
  },
  {
    id: "333f74cf-7973-4270-a7f1-2d54e59a4f18",
    name: "University YMCA 大學青年會（科大）",
    university: "hku",
    memberCount: 0,
    instagramFollowers: 681,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770565003/organizer_icon/ed171029-fc79-4771-a732-a0908492e6a6.jpg",
  },
  {
    id: "2b4971e4-7141-4563-b48d-68681bf07b0a",
    name: "United College, CUHK",
    university: "cuhk",
    memberCount: 0,
    instagramFollowers: 985,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1765782591/organizer_icon/b793be6c-19b9-4c0f-bfe0-d47e996cda9f.jpg",
  },
  {
    id: "cfd7fdd8-fbd8-4bc8-b2e5-df6519ec175c",
    name: "ѕιєηтє ✶⋆.˚𝟸𝟸ɴᴅ sʏᴜ ᴅᴀɴsᴏ",
    university: "hksyu",
    memberCount: 0,
    instagramFollowers: 860,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1765783089/organizer_icon/076947ed-7007-42c5-93f6-8181d61b6da5.jpg",
  },
  {
    id: "ff6cb6e1-d0fa-407b-a377-84a1dd0fa24c",
    name: "HKU School of Nursing",
    university: "hku",
    memberCount: 0,
    instagramFollowers: 777,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1765783096/organizer_icon/b9be5269-dee5-4e51-a487-11ad3b45f542.jpg",
  },
  {
    id: "51bd1992-ebad-421e-946c-806f21f30741",
    name: "香港樹仁大學歷史學系",
    university: "hksyu",
    memberCount: 0,
    instagramFollowers: 172,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770565207/organizer_icon/301a992e-e329-4c99-8e3b-636fafdd8da1.jpg",
  },
  {
    id: "82d37ddf-2a37-4e93-a40d-974ee4627b98",
    name: "PolyU SPEED",
    university: "polyu",
    memberCount: 0,
    instagramFollowers: 1419,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1765783101/organizer_icon/a6dca835-0ec3-40a1-9868-53dec9bf91d3.jpg",
  },
  {
    id: "db7beef2-3ef5-4283-9659-65c6fab60a77",
    name: "𝐈𝐧𝐧𝐨𝐏𝐨𝐫𝐭 at CUHK KTO",
    university: "cuhk",
    memberCount: 0,
    instagramFollowers: 796,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1765783106/organizer_icon/72cf6778-0d85-411b-ad89-e69010bc595f.jpg",
  },
  {
    id: "54d7d44e-6f0a-4892-b637-e5cd0b12232e",
    name: "CCA, EdUHK 香港教育大學-文化與創意藝術學系🎨🎶",
    university: "eduhk",
    memberCount: 0,
    instagramFollowers: 790,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1765894484/organizer_icon/3edd938d-dbaf-4e5b-9001-ec971935ced6.jpg",
  },
  {
    id: "db4dc815-3cfc-4b01-81cc-6236d8ef717a",
    name: "Aurelia, 34th UST Danso 🪼",
    university: "hku",
    memberCount: 0,
    instagramFollowers: 1257,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1765894499/organizer_icon/c79d52fd-f8cb-412a-8cd9-00360961ecc2.jpg",
  },
  {
    id: "f4d4a966-b9d9-4324-98df-a3897b6d3f9c",
    name: "สมาคมนักเรียนไทยใน HKU | HKU Thai Student Association",
    university: "hku",
    memberCount: 0,
    instagramFollowers: 43,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770565230/organizer_icon/91533d48-4e46-46a9-bed7-9f2f7bbe272b.jpg",
  },
  {
    id: "7a78306f-5acb-48c6-bdaf-804300e85cf2",
    name: "新唱二六 魂牽新弦",
    university: null,
    memberCount: 0,
    instagramFollowers: 128,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1765894507/organizer_icon/918e4849-106c-4a97-bbf1-813409c8c820.jpg",
  },
  {
    id: "ef4dc359-f276-4b0f-93cc-3bd34851e4c3",
    name: "都大音樂｜HKMU MUSIC SOCIETY",
    university: "hkmu",
    memberCount: 0,
    instagramFollowers: 320,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1765894526/organizer_icon/9c0ad908-e293-4bf5-865c-3b68d920c344.jpg",
  },
  {
    id: "9fbde834-ca9a-4c53-b007-a669ea983de0",
    name: "香港教育大學 文化與創意藝術學系",
    university: "eduhk",
    memberCount: 0,
    instagramFollowers: 123,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1764394725/organizer_icon/a0c03cad-ea68-48a3-8f26-88986012a871.jpg",
  },
  {
    id: "363570d1-0683-415b-a401-2077db62ae86",
    name: "LingU Men’s Soccer Team",
    university: "lingu",
    memberCount: 0,
    instagramFollowers: 120,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770565157/organizer_icon/0b3f7899-3cb3-4ca6-8914-2c509a4f4fac.jpg",
  },
  {
    id: "60e2f344-d1da-40e0-8644-09792a7d795f",
    name: "遊趣Gampus",
    university: "eduhk",
    memberCount: 0,
    instagramFollowers: 37,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770565121/organizer_icon/a8be8224-ba8c-48e1-84f0-ce0609d54528.jpg",
  },
  {
    id: "09cd12ae-056d-4584-8002-9c2c710c2104",
    name: "中大健身 | 𝗖𝗨𝗛𝗞 𝗚𝘆𝗺𝗦𝗼𝗰",
    university: "cuhk",
    memberCount: 0,
    instagramFollowers: 36,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770565182/organizer_icon/3ca1bf02-abc3-4c44-afbc-cc329bb44484.jpg",
  },
  {
    id: "3a42c2ae-15e2-44a0-bc56-112892ee751d",
    name: "中文大學 國是學會",
    university: "cuhk",
    memberCount: 0,
    instagramFollowers: 31,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1770565039/organizer_icon/bc76eda0-928d-442d-b5c9-934ecd008491.jpg",
  },
  {
    id: "69d563d3-cbed-4a0f-aee9-130dd409114f",
    name: "新皞 Spera",
    university: "cuhk",
    memberCount: 0,
    instagramFollowers: 39,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1764394721/organizer_icon/bc0b1647-d18e-4436-954a-855333b85ed5.jpg",
  },
  {
    id: "d2b7af05-f027-4049-8072-587abcac5405",
    name: "運火恆承🔥Sporternity♾️💜",
    university: "cuhk",
    memberCount: 0,
    instagramFollowers: 31,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1764394138/organizer_icon/903db319-9e8d-44bf-a5fb-59abca8539c4.jpg",
  },
  {
    id: "c8ae2e72-f281-49b6-b9b6-56040133298f",
    name: "🔱Psyscendent❤️‍🔥心燏♥️",
    university: "cuhk",
    memberCount: 0,
    instagramFollowers: 28,
    icon: "http://res.cloudinary.com/dt0cvvayr/image/upload/v1764394682/organizer_icon/a4ecfabc-b860-4cbf-bab1-56b6eaa0d5c7.jpg",
  },
];
